/**
 * ADR-0240 Agency Portal v2 Slice 6 — cross-run Reports table.
 *
 * v4 UX (2026-10-10) — founder feedback:
 *
 *   - the per-import "upload log" / toggle has returned as an "Import"
 *     filter in the toolbar + an Imports side panel listing every
 *     batch (date, N domains, counts). Clicking one filters the table.
 *   - Remove works on EVERY row with a single ConfirmDialog, not a
 *     two-click arm/confirm. Cross-origin DELETE preflight CORS
 *     already answered on the API side.
 *   - Bulk-select via per-row checkbox + a header "select all on this
 *     page" affordance + a sticky "Delete N" action in the toolbar.
 *
 * Columns (spec §6.1):
 *   1. Checkbox (bulk delete + CSV export)
 *   2. Website + company
 *   3. Agent-ready checks (`N of M pass` + bar)
 *   4. Status pill
 *   5. Scanned
 *   6. Emailed
 *   7. Actions: Open ↗, overflow (Copy link · Email · Re-scan · Remove)
 *
 * Live updates: polls `GET /v1/agency/reports/rows` every 5 s while
 * any visible row is `queued` / `running`; stops otherwise. Paused
 * when `document.hidden`.
 */

import {
  ArrowUpRight,
  Check,
  ChevronDown,
  Copy,
  Download,
  Filter as FilterIcon,
  Layers,
  Mail,
  RotateCw,
  Search as SearchIcon,
  Trash2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { HttpClient } from '../../api/http-client.js';
import {
  bulkRemoveReportRows,
  exportReportsRowsCsv,
  getReportsOverview,
  listReportsRows,
  removeReportRow,
  retryReportRow,
  type AgencyReportsOverviewWire,
  type BulkRemoveRowsResult,
  type BulkRunSummaryWire,
  type RemoveRowResult,
  type ReportTableRow,
  type ReportTableStatusFilter,
  type RetryRowResult,
} from '../../api/agency-reports.js';
import {
  Button,
  ConfirmDialog,
  Drawer,
  InlineNotice,
  PopoverMenu,
  PopoverMenuItem,
} from '../../components/atoms/index.js';
import { Tooltip } from '../../components/atoms/Tooltip.js';
import { mapReason } from '../../lib/humanize-agency-error.js';
import { useC } from '../../lib/theme.js';
import { useIsMobile } from '../../lib/use-is-mobile.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

const POLL_INTERVAL_MS = 5000;

/** v4 (2026-10-10) — grid template now carries a leading 32px select
 *  column + an unchanged ordering of the rest. */
const GRID_TEMPLATE = '32px minmax(180px, 1fr) 100px 110px 80px 80px 150px';

export interface ReportsTableProps {
  readonly http: HttpClient;
  readonly onEmail: (row: ReportTableRow) => void;
  /**
   * Agency Portal v4 UX (2026-10-11) — focus the table on a specific
   * run_id. Set by `<AgencyReports />` when the "View results" button
   * in the import modal is clicked after a submit with skipped rows:
   * the modal closes, the table filters to the just-created run, and
   * the parent scrolls the table into view. The value is read only
   * on CHANGE (strict identity) — passing the same string twice is a
   * no-op, so the table's internal `run_filter` state stays owner.
   */
  readonly focusRunId?: string | null;
}

export function ReportsTable({ http, onEmail, focusRunId }: ReportsTableProps): JSX.Element {
  const C = useC();
  const is_mobile = useIsMobile();
  const [rows, setRows] = useState<readonly ReportTableRow[]>([]);
  const [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [prev_cursors, setPrevCursors] = useState<readonly string[]>([]);
  const [page_size, setPageSize] = useState(25);
  const [status, setStatus] = useState<ReportTableStatusFilter>('all');
  const [score_bucket, setScoreBucket] = useState<
    'any' | 'all_pass' | 'most' | 'some' | 'few'
  >('any');
  const [date_bucket, setDateBucket] = useState<'7' | '30' | '90' | 'all'>('30');
  const [q, setQ] = useState('');
  const [run_filter, setRunFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retry_notice, setRetryNotice] = useState<string | null>(null);
  const [copied_row_id, setCopiedRowId] = useState<string | null>(null);
  const [retry_in_flight, setRetryInFlight] = useState<string | null>(null);
  const [remove_in_flight, setRemoveInFlight] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [include_contact, setIncludeContact] = useState(false);
  const [confirm_remove_row, setConfirmRemoveRow] = useState<ReportTableRow | null>(null);
  const [confirm_bulk_remove, setConfirmBulkRemove] = useState(false);
  const [bulk_removing, setBulkRemoving] = useState(false);
  const [selected_ids, setSelectedIds] = useState<ReadonlyArray<string>>([]);
  const [imports_open, setImportsOpen] = useState(false);
  const [runs, setRuns] = useState<readonly BulkRunSummaryWire[]>([]);
  const q_timer = useRef<number | null>(null);

  // Agency Portal v4 UX (2026-10-11) — accept a parent-driven focus run.
  // We mirror the prop into `run_filter` only when it changes to a NEW,
  // non-null value so the user can still clear or change the filter
  // through the dropdown without the prop snapping it back. The ref
  // guards against the "same prop twice" re-render case. We also
  // refresh the `runs` list so the Import dropdown reflects the newly
  // imported run instead of showing "All" with a 2-row sub-set.
  const last_focus_run_id = useRef<string | null>(null);
  useEffect(() => {
    if (focusRunId === undefined || focusRunId === null) return;
    if (focusRunId === last_focus_run_id.current) return;
    last_focus_run_id.current = focusRunId;
    setRunFilter(focusRunId);
    void (async () => {
      const r = await getReportsOverview(http);
      if (r.kind === 'ok') setRuns(r.overview.runs);
    })();
  }, [focusRunId, http]);

  const score_filter = useMemo(() => {
    switch (score_bucket) {
      case 'all_pass':
        return { min_score: 1, max_score: 1 } as const;
      case 'most':
        return { min_score: 0.75, max_score: 1 } as const;
      case 'some':
        return { min_score: 0.25, max_score: 0.74 } as const;
      case 'few':
        return { min_score: 0, max_score: 0.24 } as const;
      case 'any':
        return { min_score: null, max_score: null } as const;
    }
  }, [score_bucket]);

  const date_filter = useMemo(() => {
    const now_ms = Date.now();
    const day_ms = 24 * 60 * 60 * 1000;
    switch (date_bucket) {
      case '7':
        return { from: new Date(now_ms - 7 * day_ms).toISOString() } as const;
      case '30':
        return { from: new Date(now_ms - 30 * day_ms).toISOString() } as const;
      case '90':
        return { from: new Date(now_ms - 90 * day_ms).toISOString() } as const;
      case 'all':
        return { from: null } as const;
    }
  }, [date_bucket]);

  const load = useCallback(
    async (opts: { cursor?: string | null; reset_prev?: boolean } = {}): Promise<void> => {
      setLoading(true);
      const r = await listReportsRows(http, {
        page_size,
        cursor: opts.cursor ?? null,
        status,
        min_score: score_filter.min_score,
        max_score: score_filter.max_score,
        from: date_filter.from,
        q: q.trim().length >= 2 ? q.trim() : null,
        run_id: run_filter,
      });
      setLoading(false);
      if (r.kind === 'ok') {
        setRows(r.result.rows);
        setTotal(r.result.total);
        setCursor(r.result.next_cursor);
        setError(null);
        if (opts.reset_prev === true) setPrevCursors([]);
      } else if (r.kind === 'rate_limited') {
        setError(r.message);
      } else if (r.kind !== 'forbidden') {
        setError(r.message);
      }
    },
    [http, page_size, status, score_filter, date_filter, q, run_filter],
  );

  // Load runs on mount so the Import filter dropdown has data.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await getReportsOverview(http);
      if (cancelled) return;
      if (r.kind === 'ok') setRuns(r.overview.runs);
    })();
    return () => {
      cancelled = true;
    };
  }, [http]);

  // Reset to first page whenever a filter changes.
  useEffect(() => {
    void load({ cursor: null, reset_prev: true });
    setSelectedIds([]);
  }, [status, score_bucket, date_bucket, page_size, run_filter, load]);

  // Debounce the search string.
  useEffect(() => {
    if (q_timer.current !== null) window.clearTimeout(q_timer.current);
    q_timer.current = window.setTimeout(() => {
      void load({ cursor: null, reset_prev: true });
    }, 300);
    return () => {
      if (q_timer.current !== null) window.clearTimeout(q_timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Live updates: poll while any visible row is queued / running.
  useEffect(() => {
    const any_pending = rows.some((r) => r.status === 'queued' || r.status === 'running');
    if (!any_pending) return;
    const tick = (): void => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void load({ cursor: null, reset_prev: false });
    };
    const timer = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const onNextPage = useCallback((): void => {
    if (cursor === null) return;
    setPrevCursors((prev) => [...prev, cursor]);
    void load({ cursor });
  }, [cursor, load]);

  const onPrevPage = useCallback((): void => {
    if (prev_cursors.length === 0) {
      void load({ cursor: null });
      return;
    }
    const next_prev = prev_cursors.slice(0, -1);
    setPrevCursors(next_prev);
    const prev_cursor = next_prev[next_prev.length - 1] ?? null;
    void load({ cursor: prev_cursor });
  }, [prev_cursors, load]);

  const onCopyLink = useCallback(async (row: ReportTableRow): Promise<void> => {
    if (row.report_url === null) return;
    try {
      await navigator.clipboard.writeText(row.report_url);
      setCopiedRowId(row.row_id);
      window.setTimeout(() => setCopiedRowId(null), 1500);
    } catch {
      /* clipboard denied; silent */
    }
  }, []);

  const onRetry = useCallback(async (row: ReportTableRow): Promise<void> => {
    setRetryInFlight(row.row_id);
    const r = await retryReportRow(http, row.row_id);
    setRetryInFlight(null);
    setRetryNotice(retryCopy(r, row));
    if (r.kind === 'ok') await load({ cursor: null });
  }, [http, load]);

  // v4 (2026-10-10) — one Remove path. ConfirmDialog gates every
  // delete; the server allow-set is open on done/failed/rate_limited/
  // invalid_domain. The dashboard only hides Remove on queued/running.
  const onConfirmRemoveOne = useCallback(async (): Promise<void> => {
    const row = confirm_remove_row;
    if (row === null) return;
    setConfirmRemoveRow(null);
    setRemoveInFlight(row.row_id);
    const r = await removeReportRow(http, row.row_id);
    setRemoveInFlight(null);
    setRetryNotice(removeCopy(r, row));
    if (r.kind === 'ok') {
      setSelectedIds((prev) => prev.filter((id) => id !== row.row_id));
      await load({ cursor: null });
    }
  }, [confirm_remove_row, http, load]);

  const onConfirmBulkRemove = useCallback(async (): Promise<void> => {
    setConfirmBulkRemove(false);
    if (selected_ids.length === 0) return;
    setBulkRemoving(true);
    const r = await bulkRemoveReportRows(http, selected_ids);
    setBulkRemoving(false);
    setRetryNotice(bulkRemoveCopy(r));
    if (r.kind === 'ok') {
      setSelectedIds([]);
      await load({ cursor: null });
    }
  }, [http, selected_ids, load]);

  const onExport = useCallback(async (): Promise<void> => {
    setExporting(true);
    const r = await exportReportsRowsCsv(http, {
      status,
      min_score: score_filter.min_score,
      max_score: score_filter.max_score,
      from: date_filter.from,
      q: q.trim().length >= 2 ? q.trim() : null,
      run_id: run_filter,
      include_contact,
    });
    setExporting(false);
    if (r.kind !== 'ok') {
      setRetryNotice(r.kind === 'rate_limited' ? r.message : r.message);
      return;
    }
    try {
      const url = URL.createObjectURL(r.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `agency-reports-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      /* non-DOM env */
    }
  }, [http, status, score_filter, date_filter, q, run_filter, include_contact]);

  const page_count = Math.ceil(total / page_size);
  const current_page = prev_cursors.length + 1;
  const start_num = total === 0 ? 0 : (current_page - 1) * page_size + 1;
  const end_num = Math.min(current_page * page_size, total);

  const selectable_ids = useMemo(
    () => rows.filter((r) => r.status !== 'queued' && r.status !== 'running').map((r) => r.row_id),
    [rows],
  );
  const all_on_page_selected =
    selectable_ids.length > 0 && selectable_ids.every((id) => selected_ids.includes(id));
  const togglePageSelection = (): void => {
    if (all_on_page_selected) {
      setSelectedIds((prev) => prev.filter((id) => !selectable_ids.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...selectable_ids])));
    }
  };
  const toggleRow = (row_id: string): void => {
    setSelectedIds((prev) =>
      prev.includes(row_id) ? prev.filter((id) => id !== row_id) : [...prev, row_id],
    );
  };

  const activeRun = runs.find((r) => r.run_id === run_filter) ?? null;

  // v5 toolbar (2026-10-11) — Search + Filters popover + Export menu.
  // The original row crammed six controls across the toolbar; this
  // collapses Status / Checks passed / Date / Import into one popover
  // with removable chips below. The filter semantics (and the API they
  // drive) are unchanged — only the entry surface moves.
  const activeFilters: ReadonlyArray<{
    readonly key: 'status' | 'score' | 'date' | 'run';
    readonly label: string;
    readonly clear: () => void;
    readonly testid: string;
  }> = useMemo(() => {
    const out: Array<{ key: 'status' | 'score' | 'date' | 'run'; label: string; clear: () => void; testid: string }> = [];
    if (status !== 'all') {
      out.push({
        key: 'status',
        label: `Status: ${statusFilterLabel(status)}`,
        clear: () => setStatus('all'),
        testid: 'reports-filter-chip-status',
      });
    }
    if (score_bucket !== 'any') {
      out.push({
        key: 'score',
        label: `Checks passed: ${scoreBucketLabel(score_bucket)}`,
        clear: () => setScoreBucket('any'),
        testid: 'reports-filter-chip-score',
      });
    }
    if (date_bucket !== '30') {
      out.push({
        key: 'date',
        label: `Scanned: ${dateBucketLabel(date_bucket)}`,
        clear: () => setDateBucket('30'),
        testid: 'reports-filter-chip-date',
      });
    }
    if (run_filter !== null) {
      out.push({
        key: 'run',
        label: `Import: ${activeRun !== null ? formatImportDate(activeRun.created_at) : 'selected'}`,
        clear: () => setRunFilter(null),
        testid: 'reports-filter-chip-run',
      });
    }
    return out;
  }, [status, score_bucket, date_bucket, run_filter, activeRun]);

  const filtersBtnRef = useRef<HTMLButtonElement | null>(null);
  const exportBtnRef = useRef<HTMLButtonElement | null>(null);
  const [filters_open, setFiltersOpen] = useState(false);
  const [export_open, setExportOpen] = useState(false);

  return (
    <div data-testid="reports-table-root">
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {/* Search grows to fill the row — the primary tool. */}
        <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 220, maxWidth: 420 }}>
          <SearchIcon
            size={13}
            color={C.muted}
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}
          />
          <input
            type="search"
            data-testid="reports-table-search"
            placeholder="Search domain or company"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{
              height: 34,
              padding: '0 10px 0 28px',
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              background: C.surface,
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              width: '100%',
            }}
          />
        </div>

        <button
          type="button"
          ref={filtersBtnRef}
          onClick={() => setFiltersOpen((v) => !v)}
          aria-expanded={filters_open}
          aria-haspopup="menu"
          data-testid="reports-table-filters"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            height: 34,
            padding: '0 12px',
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            background: activeFilters.length > 0 ? C.limeDim : C.surface,
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
            cursor: 'pointer',
          }}
        >
          <FilterIcon size={13} aria-hidden />
          <span>Filters</span>
          {activeFilters.length > 0 ? (
            <span
              data-testid="reports-table-filters-badge"
              style={{
                marginLeft: 4,
                background: C.lime,
                color: C.ink,
                borderRadius: 999,
                fontFamily: monoFont,
                fontSize: 10,
                fontWeight: 600,
                padding: '1px 6px',
                lineHeight: 1.4,
              }}
            >
              {activeFilters.length}
            </span>
          ) : null}
          <ChevronDown size={11} aria-hidden style={{ marginLeft: 2 }} />
        </button>

        <Button
          variant="ghost"
          size="sm"
          icon={Layers}
          onClick={() => setImportsOpen(true)}
          data-testid="reports-table-imports-open"
        >
          Imports
        </Button>

        <div style={{ flex: 1 }} />

        <div
          data-testid="reports-table-total"
          style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, whiteSpace: 'nowrap' }}
        >
          {loading ? 'Loading…' : `${total.toLocaleString('en-US')} ${total === 1 ? 'row' : 'rows'}`}
        </div>

        <button
          type="button"
          ref={exportBtnRef}
          onClick={() => setExportOpen((v) => !v)}
          aria-expanded={export_open}
          aria-haspopup="menu"
          disabled={exporting}
          data-testid="reports-table-export"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            height: 34,
            padding: '0 12px',
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            background: C.surface2,
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
            cursor: exporting ? 'wait' : 'pointer',
            opacity: exporting ? 0.6 : 1,
          }}
        >
          <Download size={13} aria-hidden />
          <span>{exporting ? 'Exporting…' : 'Export'}</span>
          <ChevronDown size={11} aria-hidden style={{ marginLeft: 2 }} />
        </button>
      </div>

      {/* Removable filter chips row. Hidden when no filters are active. */}
      {activeFilters.length > 0 ? (
        <div
          data-testid="reports-table-active-filters"
          style={{
            display: 'flex',
            gap: 6,
            marginBottom: 10,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {activeFilters.map((chip) => (
            <span
              key={chip.key}
              data-testid={chip.testid}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                height: 24,
                padding: '0 4px 0 10px',
                border: `1px solid ${C.border}`,
                borderRadius: 999,
                background: C.surface2,
                fontFamily: sansFont,
                fontSize: 12,
                color: C.text,
              }}
            >
              <span>{chip.label}</span>
              <button
                type="button"
                aria-label={`Remove filter: ${chip.label}`}
                onClick={chip.clear}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 18,
                  height: 18,
                  border: 'none',
                  background: 'transparent',
                  color: C.muted,
                  cursor: 'pointer',
                  borderRadius: 999,
                  padding: 0,
                }}
              >
                <X size={11} aria-hidden />
              </button>
            </span>
          ))}
          <button
            type="button"
            data-testid="reports-table-clear-filters"
            onClick={() => {
              setStatus('all');
              setScoreBucket('any');
              setDateBucket('30');
              setRunFilter(null);
            }}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '0 6px',
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Clear all
          </button>
        </div>
      ) : null}

      {/* Filters popover — a compact column of labelled selects. The
          existing filter state (status, score_bucket, date_bucket,
          run_filter) stays the owner; selects write straight to it. */}
      <PopoverMenu
        open={filters_open}
        onClose={() => setFiltersOpen(false)}
        anchorRef={filtersBtnRef}
        align="start"
        minWidth={280}
        testid="reports-table-filters-popover"
        ariaLabel="Reports filters"
      >
        <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <FilterGroup label="Status" C={C}>
            <select
              data-testid="reports-table-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as ReportTableStatusFilter)}
              style={filterSelectStyle(C)}
            >
              <option value="all">All</option>
              <option value="ready">Ready</option>
              <option value="scanning">Scanning or queued</option>
              <option value="failed">Failed</option>
              <option value="rate_limited">Over daily limit</option>
              <option value="emailed">Emailed</option>
              <option value="not_emailed">Not emailed</option>
            </select>
          </FilterGroup>
          <FilterGroup label="Checks passed" C={C}>
            <select
              data-testid="reports-table-score"
              value={score_bucket}
              onChange={(e) => setScoreBucket(e.target.value as typeof score_bucket)}
              style={filterSelectStyle(C)}
            >
              <option value="any">Any</option>
              <option value="all_pass">All pass</option>
              <option value="most">Most pass (≥ 75%)</option>
              <option value="some">Some pass (25–74%)</option>
              <option value="few">Few pass (&lt; 25%)</option>
            </select>
          </FilterGroup>
          <FilterGroup label="Scanned" C={C}>
            <select
              data-testid="reports-table-date"
              value={date_bucket}
              onChange={(e) => setDateBucket(e.target.value as typeof date_bucket)}
              style={filterSelectStyle(C)}
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </FilterGroup>
          <FilterGroup label="Import" C={C}>
            <select
              data-testid="reports-table-run"
              value={run_filter ?? ''}
              onChange={(e) => setRunFilter(e.target.value === '' ? null : e.target.value)}
              style={filterSelectStyle(C)}
            >
              <option value="">All imports</option>
              {runs.map((r) => (
                <option key={r.run_id} value={r.run_id}>
                  {formatImportLabel(r)}
                </option>
              ))}
            </select>
          </FilterGroup>
        </div>
      </PopoverMenu>

      {/* Export menu. Hosts the include-contact toggle (previously a
          loose checkbox in the toolbar) so the toolbar reads one row. */}
      <PopoverMenu
        open={export_open}
        onClose={() => setExportOpen(false)}
        anchorRef={exportBtnRef}
        align="end"
        minWidth={240}
        testid="reports-table-export-popover"
        ariaLabel="Export options"
      >
        <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label
            data-testid="reports-table-include-contact"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontFamily: sansFont,
              fontSize: 12,
              color: C.text,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={include_contact}
              onChange={(e) => setIncludeContact(e.target.checked)}
            />
            Include contact details
          </label>
          <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted }}>
            {activeFilters.length === 0
              ? 'Exports every row matching the current search.'
              : `Exports ${total.toLocaleString('en-US')} ${total === 1 ? 'row' : 'rows'} matching your filters.`}
          </div>
          <Button
            variant="primary"
            size="sm"
            icon={Download}
            loading={exporting}
            onClick={() => {
              setExportOpen(false);
              void onExport();
            }}
            data-testid="reports-table-export-confirm"
          >
            Export CSV
          </Button>
        </div>
      </PopoverMenu>

      {activeRun !== null ? (
        <div
          data-testid="reports-table-active-import"
          style={{
            marginBottom: 10,
            padding: '8px 12px',
            background: C.surface2,
            border: `1px solid ${C.borderSoft}`,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            fontFamily: sansFont,
            fontSize: 12,
            color: C.text,
          }}
        >
          <span style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.muted, textTransform: 'uppercase' }}>
            Import
          </span>
          <span>{formatImportLabel(activeRun)}</span>
          <div style={{ flex: 1 }} />
          <Button variant="ghost" size="sm" onClick={() => setRunFilter(null)}>
            Show all imports
          </Button>
        </div>
      ) : null}

      {/* v4 (2026-10-10) — bulk-select toolbar. Appears only when at
          least one row is checked. Primary action is Delete; a sticky
          "Clear" un-selects everything. */}
      {selected_ids.length > 0 ? (
        <div
          data-testid="reports-table-bulk-bar"
          style={{
            marginBottom: 10,
            padding: '8px 12px',
            background: C.amberDim ?? C.surface2,
            border: `1px solid ${C.border}`,
            borderLeft: `3px solid ${C.amberFg ?? C.lime}`,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text }}>
            {selected_ids.length === 1
              ? '1 row selected'
              : `${selected_ids.length} rows selected`}
          </span>
          <div style={{ flex: 1 }} />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedIds([])}
            data-testid="reports-table-bulk-clear"
          >
            Clear
          </Button>
          <Button
            variant="danger"
            size="sm"
            icon={Trash2}
            onClick={() => setConfirmBulkRemove(true)}
            loading={bulk_removing}
            data-testid="reports-table-bulk-delete"
          >
            Delete selected
          </Button>
        </div>
      ) : null}

      {error !== null ? (
        <div style={{ marginBottom: 12 }}>
          <InlineNotice tone="error" title="Could not load reports">
            {error}
          </InlineNotice>
        </div>
      ) : null}

      {retry_notice !== null ? (
        <div style={{ marginBottom: 12 }}>
          <InlineNotice tone="info">{retry_notice}</InlineNotice>
        </div>
      ) : null}

      <div
        role="table"
        data-testid="reports-table"
        data-layout={is_mobile ? 'card' : 'grid'}
        style={{
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {!is_mobile ? (
          <div
            role="row"
            style={{
              display: 'grid',
              gridTemplateColumns: GRID_TEMPLATE,
              gap: 10,
              padding: '8px 12px',
              background: C.surface2,
              borderBottom: `1px solid ${C.border}`,
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              textTransform: 'uppercase',
              alignItems: 'center',
            }}
          >
            <div role="columnheader">
              <input
                type="checkbox"
                aria-label="Select all on page"
                data-testid="reports-table-select-all"
                checked={all_on_page_selected}
                onChange={togglePageSelection}
                disabled={selectable_ids.length === 0}
              />
            </div>
            <div role="columnheader">Website</div>
            <div role="columnheader">
              <Tooltip content="How many of the agent-readiness checks this site passes. Same count as the report headline.">
                <span>Checks passed</span>
              </Tooltip>
            </div>
            <div role="columnheader">Status</div>
            <div role="columnheader">Scanned</div>
            <div role="columnheader">Emailed</div>
            <div role="columnheader" style={{ textAlign: 'right' }}>
              Actions
            </div>
          </div>
        ) : null}
        {rows.length === 0 && !loading ? (
          <div
            style={{
              padding: '20px 12px',
              fontFamily: sansFont,
              fontSize: 13,
              color: C.muted,
            }}
          >
            No reports match your filters.
          </div>
        ) : null}
        {rows.map((row) =>
          is_mobile ? (
            <MobileRow
              key={row.row_id}
              row={row}
              selected={selected_ids.includes(row.row_id)}
              copied={copied_row_id === row.row_id}
              retry_busy={retry_in_flight === row.row_id}
              remove_busy={remove_in_flight === row.row_id}
              onSelectToggle={() => toggleRow(row.row_id)}
              onCopy={() => void onCopyLink(row)}
              onEmail={() => onEmail(row)}
              onRetry={() => void onRetry(row)}
              onRemove={() => setConfirmRemoveRow(row)}
            />
          ) : (
            <TableRow
              key={row.row_id}
              row={row}
              selected={selected_ids.includes(row.row_id)}
              copied={copied_row_id === row.row_id}
              retry_busy={retry_in_flight === row.row_id}
              remove_busy={remove_in_flight === row.row_id}
              onSelectToggle={() => toggleRow(row.row_id)}
              onCopy={() => void onCopyLink(row)}
              onEmail={() => onEmail(row)}
              onRetry={() => void onRetry(row)}
              onRemove={() => setConfirmRemoveRow(row)}
            />
          ),
        )}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 2px',
          fontFamily: monoFont,
          fontSize: 11,
          color: C.muted,
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <div data-testid="reports-table-pagination">
          {total === 0 ? '0 rows' : `${start_num.toLocaleString('en-US')}–${end_num.toLocaleString('en-US')} of ${total.toLocaleString('en-US')}`}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            data-testid="reports-table-page-size"
            value={page_size}
            onChange={(e) => setPageSize(Number(e.target.value))}
            style={{ ...filterSelectStyle(C), height: 28, fontSize: 11 }}
          >
            <option value={25}>25 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
          <Button
            size="sm"
            variant="ghost"
            disabled={prev_cursors.length === 0}
            onClick={onPrevPage}
            data-testid="reports-table-prev"
          >
            ‹ Prev
          </Button>
          <span data-testid="reports-table-page" style={{ whiteSpace: 'nowrap' }}>
            Page {current_page}
            {page_count > 0 ? ` of ${page_count}` : ''}
          </span>
          <Button
            size="sm"
            variant="ghost"
            disabled={cursor === null}
            onClick={onNextPage}
            data-testid="reports-table-next"
          >
            Next ›
          </Button>
        </div>
      </div>

      {/* v4 (2026-10-10) — Imports side panel. Lists every recent run
          with status counts; clicking filters the table. */}
      <Drawer open={imports_open} onClose={() => setImportsOpen(false)} title="Imports">
        <div style={{ padding: 20 }}>
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.muted,
              lineHeight: 1.5,
              marginBottom: 12,
            }}
          >
            Each batch you submit shows here. Pick one to filter the Reports
            table to that import.
          </p>
          {runs.length === 0 ? (
            <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
              No imports yet.
            </div>
          ) : (
            <ul data-testid="reports-table-imports-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {runs.map((r) => {
                const active = run_filter === r.run_id;
                return (
                  <li
                    key={r.run_id}
                    style={{
                      padding: 12,
                      marginBottom: 8,
                      border: `1px solid ${active ? C.lime : C.borderSoft}`,
                      borderLeft: `3px solid ${active ? C.lime : C.borderSoft}`,
                      borderRadius: 2,
                      cursor: 'pointer',
                      background: active ? C.surface2 : 'transparent',
                    }}
                    onClick={() => {
                      setRunFilter(r.run_id);
                      setImportsOpen(false);
                    }}
                    data-testid="reports-table-imports-item"
                  >
                    <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500 }}>
                      {formatImportDate(r.created_at)} · {r.domains_accepted} of {r.domains_submitted} domains
                    </div>
                    <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, marginTop: 4 }}>
                      {describeCounts(r)}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {run_filter !== null ? (
            <div style={{ marginTop: 12 }}>
              <Button variant="ghost" size="sm" onClick={() => { setRunFilter(null); setImportsOpen(false); }}>
                Show all imports
              </Button>
            </div>
          ) : null}
        </div>
      </Drawer>

      {/* v4 (2026-10-10) — single-row delete confirm. One modal, one
          action, no two-click arm. Mentions "share link stops working"
          on done rows so the agency knows what breaks. */}
      <ConfirmDialog
        open={confirm_remove_row !== null}
        onClose={() => setConfirmRemoveRow(null)}
        title={confirm_remove_row !== null ? `Delete report for ${confirm_remove_row.domain}?` : ''}
        body={
          confirm_remove_row === null
            ? ''
            : confirm_remove_row.status === 'done'
              ? 'The shared link stops working. The anonymous scan snapshot stays — only your branded share link is revoked.'
              : 'This removes the row from your Reports table. You can re-import the same domain later.'
        }
        confirmLabel="Delete report"
        confirmTone="coral"
        icon={Trash2}
        onConfirm={() => void onConfirmRemoveOne()}
      />

      {/* v4 (2026-10-10) — bulk delete confirm. */}
      <ConfirmDialog
        open={confirm_bulk_remove}
        onClose={() => setConfirmBulkRemove(false)}
        title={`Delete ${selected_ids.length} ${selected_ids.length === 1 ? 'report' : 'reports'}?`}
        body="The shared links on any 'ready' rows stop working. Scan snapshots are kept."
        confirmLabel={`Delete ${selected_ids.length}`}
        confirmTone="coral"
        icon={Trash2}
        onConfirm={() => void onConfirmBulkRemove()}
      />
    </div>
  );
}

function filterSelectStyle(C: ReturnType<typeof useC>): React.CSSProperties {
  return {
    width: '100%',
    height: 32,
    padding: '0 10px',
    border: `1px solid ${C.border}`,
    borderRadius: 2,
    background: C.surface,
    fontFamily: sansFont,
    fontSize: 13,
    color: C.text,
  };
}

function FilterGroup({
  label,
  children,
  C,
}: {
  label: string;
  children: React.ReactNode;
  C: ReturnType<typeof useC>;
}): JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          letterSpacing: 2,
          textTransform: 'uppercase',
          color: C.muted,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function statusFilterLabel(s: ReportTableStatusFilter): string {
  switch (s) {
    case 'all':          return 'All';
    case 'ready':        return 'Ready';
    case 'scanning':     return 'Scanning or queued';
    case 'failed':       return 'Failed';
    case 'rate_limited': return 'Over daily limit';
    case 'emailed':      return 'Emailed';
    case 'not_emailed':  return 'Not emailed';
  }
}

function scoreBucketLabel(s: 'any' | 'all_pass' | 'most' | 'some' | 'few'): string {
  switch (s) {
    case 'any':      return 'Any';
    case 'all_pass': return 'All pass';
    case 'most':     return 'Most (≥ 75%)';
    case 'some':     return 'Some (25–74%)';
    case 'few':      return 'Few (< 25%)';
  }
}

function dateBucketLabel(d: '7' | '30' | '90' | 'all'): string {
  switch (d) {
    case '7':   return 'Last 7 days';
    case '30':  return 'Last 30 days';
    case '90':  return 'Last 90 days';
    case 'all': return 'All time';
  }
}

interface TableRowProps {
  readonly row: ReportTableRow;
  readonly selected: boolean;
  readonly copied: boolean;
  readonly retry_busy: boolean;
  readonly remove_busy: boolean;
  readonly onSelectToggle: () => void;
  readonly onCopy: () => void;
  readonly onEmail: () => void;
  readonly onRetry: () => void;
  readonly onRemove: () => void;
}

function TableRow({
  row,
  selected,
  copied,
  retry_busy,
  remove_busy,
  onSelectToggle,
  onCopy,
  onEmail,
  onRetry,
  onRemove,
}: TableRowProps): JSX.Element {
  const C = useC();
  const [overflow_open, setOverflowOpen] = useState(false);
  const overflowBtnRef = useRef<HTMLButtonElement | null>(null);
  const checks_label =
    row.signals_present !== null && row.signals_total !== null
      ? `${row.signals_present} / ${row.signals_total}`
      : '—';
  const bar_pct =
    row.signals_present !== null && row.signals_total !== null && row.signals_total > 0
      ? (row.signals_present / row.signals_total) * 100
      : 0;
  const status = statusCopy(row.status, row.last_sent_at);
  const scanned = row.completed_at !== null ? formatShortDate(row.completed_at) : '—';
  const scanned_full = row.completed_at !== null ? formatFullDate(row.completed_at) : undefined;
  const emailed = row.last_sent_at !== null ? formatShortDate(row.last_sent_at) : '—';
  const emailed_full = row.last_sent_at !== null ? formatFullDate(row.last_sent_at) : undefined;

  const now_ms = Date.now();
  const age_ms = row.completed_at !== null ? now_ms - new Date(row.completed_at).getTime() : 0;
  const row_status = row.status as ReportTableRow['status'];
  const can_retry =
    row_status === 'failed'
    || row_status === 'rate_limited'
    || (row_status === 'done' && age_ms >= 24 * 60 * 60 * 1000);
  const can_email = row_status === 'done';
  const can_copy = row.report_url !== null;
  const can_open = row.report_url !== null;
  // v4 (2026-10-10) — Remove allowed on every terminal row.
  const can_remove =
    row_status === 'done'
    || row_status === 'failed'
    || row_status === 'rate_limited'
    || row_status === 'invalid_domain';
  const can_select = can_remove;

  let primary: JSX.Element;
  if (row_status === 'done') {
    primary = (
      <Button
        variant="primary"
        size="sm"
        icon={ArrowUpRight}
        onClick={() => window.open(row.report_url ?? '#', '_blank', 'noopener')}
        data-testid="reports-table-open"
      >
        Open
      </Button>
    );
  } else if (row_status === 'invalid_domain') {
    primary = (
      <Button
        variant="secondary"
        size="sm"
        icon={Trash2}
        loading={remove_busy}
        onClick={onRemove}
        data-testid="reports-table-remove"
      >
        Remove
      </Button>
    );
  } else if (row_status === 'failed' || row_status === 'rate_limited') {
    primary = (
      <Button
        variant="secondary"
        size="sm"
        icon={RotateCw}
        loading={retry_busy}
        onClick={onRetry}
        data-testid="reports-table-retry"
      >
        Retry
      </Button>
    );
  } else if (row_status === 'queued' || row_status === 'running') {
    primary = (
      <Button variant="secondary" size="sm" disabled>
        {row_status === 'running' ? 'Scanning…' : 'Queued'}
      </Button>
    );
  } else {
    primary = (
      <Button variant="secondary" size="sm" disabled>
        —
      </Button>
    );
  }

  const anyOverflow = can_email || can_copy || can_retry || can_remove;

  return (
    <div
      role="row"
      data-testid="reports-table-row"
      data-row-id={row.row_id}
      data-domain={row.domain}
      data-selected={selected ? 'true' : 'false'}
      style={{
        display: 'grid',
        gridTemplateColumns: GRID_TEMPLATE,
        gap: 10,
        padding: '10px 12px',
        borderBottom: `1px solid ${C.borderSoft}`,
        alignItems: 'center',
        background: selected ? C.limeDim : 'transparent',
      }}
    >
      <div>
        <input
          type="checkbox"
          aria-label={`Select ${row.domain}`}
          data-testid="reports-table-row-select"
          checked={selected}
          onChange={onSelectToggle}
          disabled={!can_select}
        />
      </div>
      <div style={{ minWidth: 0 }}>
        {can_open ? (
          <a
            href={row.report_url ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
            title={row.domain} /* title-ok: full value of the ellipsised domain */
            style={{
              fontFamily: monoFont,
              fontSize: 13,
              color: C.text,
              textDecoration: 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              whiteSpace: 'nowrap',
            }}
          >
            {row.domain}
          </a>
        ) : (
          <span
            title={row.domain} /* title-ok: full value of the ellipsised domain */
            style={{
              fontFamily: monoFont,
              fontSize: 13,
              color: C.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {row.domain}
          </span>
        )}
        {row.company !== null ? (
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 11,
              color: C.muted,
              marginTop: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.company}
          </div>
        ) : null}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, whiteSpace: 'nowrap' }}>
        <span style={{ fontFamily: monoFont, fontSize: 13, color: C.text }}>
          {checks_label}
        </span>
        <div
          aria-hidden
          style={{
            height: 4,
            width: 64,
            background: C.surface2,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${bar_pct}%`,
              height: '100%',
              background: C.lime,
            }}
          />
        </div>
      </div>
      <div style={{ whiteSpace: 'nowrap' }}>
        <StatusPill status={status} />
      </div>
      <div
        title={scanned_full} /* title-ok: full date behind the short date */
        style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {scanned}
      </div>
      <div
        title={emailed_full} /* title-ok: full date behind the short date */
        style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {emailed}
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
        {primary}
        <button
          type="button"
          ref={overflowBtnRef}
          aria-label="More actions"
          aria-haspopup="menu"
          aria-expanded={overflow_open}
          disabled={!anyOverflow}
          onClick={() => setOverflowOpen((o) => !o)}
          data-testid="reports-table-overflow"
          style={{
            width: 28,
            height: 28,
            borderRadius: 2,
            border: `1px solid ${C.border}`,
            background: C.surface,
            color: C.text,
            cursor: anyOverflow ? 'pointer' : 'not-allowed',
            opacity: anyOverflow ? 1 : 0.4,
            fontSize: 16,
            lineHeight: 1,
            padding: 0,
          }}
        >
          ⋯
        </button>
        <PopoverMenu
          open={overflow_open && anyOverflow}
          onClose={() => setOverflowOpen(false)}
          anchorRef={overflowBtnRef}
          align="end"
          minWidth={200}
          testid="reports-table-overflow-menu"
          ariaLabel={`Actions for ${row.domain}`}
        >
          {can_copy ? (
            <PopoverMenuItem
              icon={copied ? Check : Copy}
              label={copied ? 'Copied' : 'Copy link'}
              onClick={() => { setOverflowOpen(false); onCopy(); }}
              testid="reports-table-copy"
            />
          ) : null}
          {can_email ? (
            <PopoverMenuItem
              icon={Mail}
              label="Email report"
              onClick={() => { setOverflowOpen(false); onEmail(); }}
              testid="reports-table-email"
            />
          ) : null}
          {can_retry && row_status === 'done' ? (
            <PopoverMenuItem
              icon={RotateCw}
              label="Re-scan"
              onClick={() => { setOverflowOpen(false); onRetry(); }}
              testid="reports-table-rescan"
            />
          ) : null}
          {can_remove ? (
            <PopoverMenuItem
              icon={Trash2}
              label="Delete report"
              tone="danger"
              onClick={() => { setOverflowOpen(false); onRemove(); }}
              testid="reports-table-overflow-remove"
            />
          ) : null}
        </PopoverMenu>
      </div>
    </div>
  );
}

function StatusPill({
  status,
}: {
  status: ReturnType<typeof statusCopy>;
}): JSX.Element {
  const colors = statusPillColors(status.tone);
  return (
    <Tooltip content={status.tooltip}>
      <span
        data-testid="reports-table-row-status"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '2px 10px',
          borderRadius: 999,
          background: colors.bg,
          color: colors.fg,
          border: `1px solid ${colors.border}`,
          fontFamily: sansFont,
          fontSize: 11,
          fontWeight: 500,
          whiteSpace: 'nowrap',
          lineHeight: 1.4,
        }}
      >
        {status.label}
      </span>
    </Tooltip>
  );
}

function MobileRow({
  row,
  selected,
  copied,
  retry_busy,
  remove_busy,
  onSelectToggle,
  onCopy,
  onEmail,
  onRetry,
  onRemove,
}: TableRowProps): JSX.Element {
  const C = useC();
  const [overflow_open, setOverflowOpen] = useState(false);
  const overflowBtnRef = useRef<HTMLButtonElement | null>(null);
  const checks_label =
    row.signals_present !== null && row.signals_total !== null
      ? `${row.signals_present} / ${row.signals_total}`
      : '—';
  const status = statusCopy(row.status, row.last_sent_at);
  const scanned = row.completed_at !== null ? formatShortDate(row.completed_at) : '—';
  const can_email = row.status === 'done';
  const now_ms = Date.now();
  const age_ms = row.completed_at !== null ? now_ms - new Date(row.completed_at).getTime() : 0;
  const can_retry =
    row.status === 'failed'
    || row.status === 'rate_limited'
    || (row.status === 'done' && age_ms >= 24 * 60 * 60 * 1000);
  const can_remove =
    row.status === 'done'
    || row.status === 'failed'
    || row.status === 'rate_limited'
    || row.status === 'invalid_domain';
  const can_select = can_remove;

  return (
    <div
      role="row"
      data-testid="reports-table-row"
      data-row-id={row.row_id}
      data-domain={row.domain}
      data-selected={selected ? 'true' : 'false'}
      data-layout="card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '12px 12px',
        borderBottom: `1px solid ${C.borderSoft}`,
        background: selected ? C.limeDim : 'transparent',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: '1 1 auto' }}>
          <input
            type="checkbox"
            aria-label={`Select ${row.domain}`}
            data-testid="reports-table-row-select"
            checked={selected}
            onChange={onSelectToggle}
            disabled={!can_select}
          />
          {row.report_url !== null ? (
            <a
              href={row.report_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontFamily: monoFont,
                fontSize: 13,
                color: C.text,
                textDecoration: 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: '1 1 auto',
                minWidth: 0,
              }}
            >
              {row.domain}
            </a>
          ) : (
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 13,
                color: C.text,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flex: '1 1 auto',
                minWidth: 0,
              }}
            >
              {row.domain}
            </span>
          )}
        </div>
        <span style={{ flex: '0 0 auto' }}>
          <StatusPill status={status} />
        </span>
      </div>
      {row.company !== null ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 11,
            color: C.muted,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.company}
        </div>
      ) : null}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          fontFamily: monoFont,
          fontSize: 11,
          color: C.muted,
        }}
      >
        <span>{checks_label}</span>
        <span>{scanned}</span>
      </div>
      <div
        style={{
          display: 'flex',
          gap: 6,
          flexWrap: 'wrap',
          marginTop: 4,
          alignItems: 'center',
        }}
      >
        {row.report_url !== null ? (
          <Button
            variant="ghost"
            size="sm"
            icon={ArrowUpRight}
            onClick={() => window.open(row.report_url ?? '#', '_blank', 'noopener')}
            data-testid="reports-table-open"
          >
            Open
          </Button>
        ) : null}
        {row.report_url !== null ? (
          <Button
            variant="ghost"
            size="sm"
            icon={copied ? Check : Copy}
            onClick={onCopy}
            data-testid="reports-table-copy"
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        ) : null}
        {can_email ? (
          <Button
            variant="secondary"
            size="sm"
            icon={Mail}
            onClick={onEmail}
            data-testid="reports-table-email"
          >
            Email
          </Button>
        ) : null}
        {can_retry || can_remove ? (
          <>
            <button
              type="button"
              ref={overflowBtnRef}
              onClick={() => setOverflowOpen((prev) => !prev)}
              aria-expanded={overflow_open}
              aria-haspopup="menu"
              aria-label="More actions"
              data-testid="reports-table-overflow"
              style={{
                height: 28,
                padding: '0 10px',
                borderRadius: 2,
                border: `1px solid ${C.border}`,
                background: C.surface,
                color: C.text,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              ⋯
            </button>
            <PopoverMenu
              open={overflow_open}
              onClose={() => setOverflowOpen(false)}
              anchorRef={overflowBtnRef}
              align="end"
              minWidth={180}
              ariaLabel={`Actions for ${row.domain}`}
            >
              {can_retry ? (
                <PopoverMenuItem
                  icon={RotateCw}
                  label="Retry scan"
                  disabled={retry_busy}
                  onClick={() => {
                    setOverflowOpen(false);
                    onRetry();
                  }}
                  testid="reports-table-retry"
                />
              ) : null}
              {can_remove ? (
                <PopoverMenuItem
                  icon={Trash2}
                  label="Delete"
                  tone="danger"
                  disabled={remove_busy}
                  onClick={() => {
                    setOverflowOpen(false);
                    onRemove();
                  }}
                  testid="reports-table-remove"
                />
              ) : null}
            </PopoverMenu>
          </>
        ) : null}
      </div>
    </div>
  );
}

function statusCopy(
  status: ReportTableRow['status'],
  last_sent_at: string | null,
): { label: string; tone: 'lime' | 'sky' | 'coral' | 'amber' | 'muted'; tooltip: string } {
  switch (status) {
    case 'done':
      return last_sent_at !== null
        ? { label: 'Emailed', tone: 'sky', tooltip: 'This report was sent by email.' }
        : { label: 'Ready', tone: 'lime', tooltip: 'Scan finished; the report is ready to share.' };
    case 'queued':
      return { label: 'Queued', tone: 'muted', tooltip: 'Waiting its turn in the daily queue.' };
    case 'running':
      return { label: 'Scanning', tone: 'sky', tooltip: 'Scanner is running now.' };
    case 'failed':
      return { label: 'Failed', tone: 'coral', tooltip: 'We could not reach this site. Retry or check the address.' };
    case 'rate_limited':
      return { label: 'Capped', tone: 'amber', tooltip: "You hit today's scan limit for this import. Retry tomorrow or Sill can raise your cap." };
    case 'invalid_domain':
      return { label: 'Bad address', tone: 'coral', tooltip: "This isn't a valid website address (e.g. an email, a word, or a mistyped URL)." };
  }
}

function statusPillColors(tone: 'lime' | 'sky' | 'coral' | 'amber' | 'muted'): { bg: string; fg: string; border: string } {
  switch (tone) {
    case 'lime':  return { bg: '#EFFFBF', fg: '#2B4600', border: '#D9FF3F' };
    case 'sky':   return { bg: '#DEEEFF', fg: '#1A365D', border: '#7FB8FF' };
    case 'amber': return { bg: '#FFF2CC', fg: '#6B4200', border: '#FFB627' };
    case 'coral': return { bg: '#FFE4DC', fg: '#8B1A00', border: '#FF6B47' };
    case 'muted': return { bg: '#EEEAE2', fg: '#5B5147', border: '#CFC7B9' };
  }
}

function formatShortDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function formatFullDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatImportDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatImportLabel(r: BulkRunSummaryWire): string {
  return `${formatImportDate(r.created_at)} · ${r.domains_accepted} of ${r.domains_submitted} domains`;
}

function describeCounts(r: BulkRunSummaryWire): string {
  const parts: string[] = [];
  if (r.counts.done > 0) parts.push(`${r.counts.done} ready`);
  if (r.counts.queued > 0) parts.push(`${r.counts.queued} queued`);
  if (r.counts.running > 0) parts.push(`${r.counts.running} running`);
  if (r.counts.failed > 0) parts.push(`${r.counts.failed} failed`);
  if (r.counts.rate_limited > 0) parts.push(`${r.counts.rate_limited} capped`);
  if (r.counts.invalid_domain > 0) parts.push(`${r.counts.invalid_domain} bad address`);
  return parts.length === 0 ? '—' : parts.join(' · ');
}

function retryCopy(r: RetryRowResult, row: ReportTableRow): string {
  switch (r.kind) {
    case 'ok':
      return `Re-scanning ${row.domain}.`;
    case 'hourly_limit_reached':
      return `Retry limit reached (${r.limit} per hour). Try again in a minute.`;
    case 'per_row_limit_reached':
      return `${row.domain} was retried ${r.limit} times today. Try again tomorrow.`;
    case 'daily_limit_reached':
      return `No scans left today. Resets in ${Math.ceil(r.retry_after_seconds / 3600)} hours.`;
    case 'row_too_young_for_rescan':
      return `Wait ${r.min_age_hours}h before re-scanning a ready row.`;
    case 'row_not_retryable':
      return `This row is already ${r.current_status} — nothing to retry.`;
    case 'not_found':
      return 'That row is gone. Refresh the table.';
    case 'rate_limited':
      return r.message;
    case 'forbidden':
      return 'You do not have permission to retry.';
    case 'error':
      return r.message;
  }
}

function removeCopy(r: RemoveRowResult, row: ReportTableRow): string {
  switch (r.kind) {
    case 'ok':
      return `Removed ${row.domain}.`;
    case 'not_found':
      return 'That row is gone. Refresh the table.';
    case 'row_not_removable':
      return `This row is ${r.current_status} — it can't be removed from here.`;
    case 'rate_limited':
      return mapReason(r.message) ?? 'Too many requests. Pause briefly and try again.';
    case 'forbidden':
      return mapReason(r.reason) ?? 'You do not have permission to remove this row.';
    case 'error':
      return mapReason(r.message) ?? "We couldn't remove this row. Try again, or contact Sill if it keeps failing.";
  }
}

function bulkRemoveCopy(r: BulkRemoveRowsResult): string {
  switch (r.kind) {
    case 'ok': {
      if (r.removed_count === 0 && r.skipped_count === 0) return 'Nothing was removed.';
      const parts: string[] = [];
      if (r.removed_count > 0) {
        parts.push(`Removed ${r.removed_count} ${r.removed_count === 1 ? 'row' : 'rows'}.`);
      }
      if (r.skipped_count > 0) {
        parts.push(`${r.skipped_count} skipped (busy or not found).`);
      }
      return parts.join(' ');
    }
    case 'empty':
      return 'Pick at least one row to delete.';
    case 'too_many_rows':
      return `Only ${r.max} rows per bulk delete. Pick fewer and try again.`;
    case 'rate_limited':
      return r.message;
    case 'forbidden':
      return mapReason(r.reason) ?? 'You do not have permission to remove rows.';
    case 'error':
      return mapReason(r.message) ?? "We couldn't remove those rows. Try again.";
  }
}
